/** Modal dialog with backdrop blur and close button. Uses React Aria for accessibility. */
import {
  Dialog as AriaDialog,
  Modal,
  ModalOverlay,
  Heading,
  Button as AriaButton,
} from 'react-aria-components';
import { X } from 'lucide-react';
import { cn } from '@/foundation/utils/cn';
import { useInterfacePortalContainer } from '@/foundation/ui/InterfacePortalContext';

const OVERLAY_ANIMATION = 'f-overlay-in 200ms cubic-bezier(0.16, 1, 0.3, 1) forwards';
const OVERLAY_EXIT = 'f-overlay-out 150ms cubic-bezier(0.16, 1, 0.3, 1) forwards';
const DIALOG_ANIMATION = 'f-dialog-in 250ms cubic-bezier(0.16, 1, 0.3, 1) forwards';
const DIALOG_EXIT = 'f-dialog-out 200ms cubic-bezier(0.16, 1, 0.3, 1) forwards';

const SIZE_CLASSES: Record<string, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
};

interface DialogProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  className?: string;
}

export function Dialog({ isOpen, onClose, title, children, size = 'xl', className }: DialogProps) {
  const portalContainer = useInterfacePortalContainer();

  return (
    <ModalOverlay
      isOpen={isOpen}
      UNSTABLE_portalContainer={portalContainer ?? undefined}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      isDismissable
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={({ isEntering, isExiting }) => ({
        background: 'var(--f-bg-overlay)',
        backdropFilter: 'blur(4px)',
        animation: isEntering ? OVERLAY_ANIMATION : isExiting ? OVERLAY_EXIT : undefined,
      })}
    >
      <Modal
        className={cn('w-full outline-none', SIZE_CLASSES[size])}
        style={({ isEntering, isExiting }) => ({
          animation: isEntering ? DIALOG_ANIMATION : isExiting ? DIALOG_EXIT : undefined,
        })}
      >
        <AriaDialog
          className={cn('overflow-hidden rounded-2xl outline-none', className)}
          style={{
            background: 'var(--f-bg-raised)',
            border: '1px solid var(--f-border)',
            boxShadow: 'var(--f-shadow-lg)',
            maxHeight: '85vh',
            overflowY: 'auto',
          }}
        >
          <div className="flex items-center justify-between p-5 pb-3">
            <Heading
              slot="title"
              className="font-bold"
              style={{
                fontSize: 'var(--f-text-lg)',
                color: 'var(--f-fg)',
                fontFamily: 'var(--f-font-display)',
              }}
            >
              {title}
            </Heading>
            <AriaButton
              onPress={() => onClose()}
              className="flex h-7 w-7 items-center justify-center rounded-lg transition-all duration-150 cursor-pointer"
              style={{ background: 'var(--f-bg-sunken)', color: 'var(--f-fg-muted)' }}
              aria-label="Close"
            >
              <X size={14} strokeWidth={2.5} />
            </AriaButton>
          </div>
          <div className="px-5 pb-5">{children}</div>
        </AriaDialog>
      </Modal>
    </ModalOverlay>
  );
}
